import {
	NODE_CATEGORIES,
	NODE_TYPE_LABELS,
	type NodeCategory,
	type NodeType,
} from "@velizhanin-dev/bot-scenario"
import { Plus } from "lucide-react"
import { type ReactElement, useState } from "react"
import { CATEGORY_APPEARANCE, CREATABLE_BLOCKS, NODE_ICONS } from "@/entities/bot-builder"
import { cn } from "@/shared/lib/utils"
import { Button } from "@/shared/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover"

const GROUP_ORDER: NodeCategory[] = ["response", "input", "condition", "action"]

const GROUPS = GROUP_ORDER.map((category) => ({
	category,
	blocks: CREATABLE_BLOCKS.filter(({ type }) => (NODE_CATEGORIES[type] ?? "action") === category),
})).filter((group) => group.blocks.length > 0)

interface AddBlockMenuProps {
	onPick: (type: Exclude<NodeType, "start">) => void
	hint?: string
	/** Своя кнопка вместо «＋ Блок», например, «Добавить узел» на холсте. */
	trigger?: ReactElement
	align?: "start" | "center" | "end"
}

export function AddBlockMenu({ onPick, hint, trigger, align = "start" }: AddBlockMenuProps) {
	const [open, setOpen] = useState(false)
	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				{trigger ?? (
					<Button size="sm" className="h-9 rounded-xl px-3.5">
						<Plus />
						Блок
					</Button>
				)}
			</PopoverTrigger>
			<PopoverContent
				side="top"
				align={align}
				sideOffset={14}
				className="max-h-[min(70vh,560px)] w-[min(92vw,560px)] overflow-y-auto rounded-2xl p-2"
			>
				{hint ? <p className="px-2 pt-1 pb-2 text-xs text-muted-foreground">{hint}</p> : null}
				{GROUPS.map(({ category, blocks }) => (
					<section key={category} className="pb-1.5">
						<h3 className="px-2 pt-1 pb-1 text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
							{CATEGORY_APPEARANCE[category].label}
						</h3>
						<div className="grid gap-0.5 sm:grid-cols-2">
							{blocks.map(({ type, description }) => {
								const Icon = NODE_ICONS[type]
								return (
									<Button
										key={type}
										type="button"
										variant="ghost"
										className="h-auto min-w-0 items-start justify-start gap-3 rounded-xl p-2 text-left whitespace-normal"
										onClick={() => {
											setOpen(false)
											onPick(type)
										}}
									>
										<span
											className={cn(
												"grid h-8 w-8 shrink-0 place-items-center rounded-lg",
												CATEGORY_APPEARANCE[category].tile,
											)}
										>
											<Icon className="h-4 w-4" />
										</span>
										<span className="min-w-0">
											<span className="block text-sm leading-5 font-medium">
												{NODE_TYPE_LABELS[type]}
											</span>
											<span className="block text-xs leading-4 text-muted-foreground">
												{description}
											</span>
										</span>
									</Button>
								)
							})}
						</div>
					</section>
				))}
			</PopoverContent>
		</Popover>
	)
}
